import APIService from "./APIService";
import RecentActivitiesAPI from "./RecentSimulationsAPI";

/**
 * Service for handling all email-related API operations
 */
const EmailAPI = {
  /**
   * Send an email with the provided data
   * @param {Object} emailData - The email data object
   * @param {string} emailData.sendTo - Email recipient
   * @param {string} emailData.subject - Email subject
   * @param {string} [emailData.emailBodyPlainText] - Plain text email content
   * @param {string} [emailData.emailBodyHtml] - HTML email content
   * @param {boolean} [emailData.isHtml=false] - Whether the email body contains HTML
   * @param {string} [emailData.messageId] - Optional ID for tracking
   * @param {string} [emailData.sophisticationLevel] - Optional sophistication level
   * @param {string} [emailData.communicationChannel] - Optional communication channel
   * @param {string} [emailData.contextAttributes] - Optional context attributes
   * @param {Array} [emailData.attachments] - Optional array of file attachments
   * @returns {Promise} - Promise that resolves with the API response
   */
  sendEmail: async (emailData) => {
    // Add metadata to the email payload
    const emailPayload = {
      sendTo: emailData.sendTo,
      subject: emailData.subject,
      timestamp: new Date().toISOString(),
      isSimulation: true,
    };

    // Handle different content input scenarios
    if (emailData.emailBodyPlainText || emailData.emailBodyHtml) {
      // New dual-field approach
      emailPayload.emailBodyPlainText = emailData.emailBodyPlainText || "";
      emailPayload.emailBodyHtml = emailData.emailBodyHtml || "";

      // Determine if HTML should be used based on presence of HTML content
      emailPayload.isHtml = !!(
        emailData.emailBodyHtml && emailData.emailBodyHtml.trim()
      );
    } else {
      throw new Error("No email content provided");
    }

    // Handle attachments if present
    if (emailData.attachments && emailData.attachments.length > 0) {
      console.log(
        `[EmailAPI] Sending email with ${emailData.attachments.length} attachments`
      );

      // In a real implementation, we would use FormData to handle file uploads
      // For the demo, we'll just log the attachment info
      emailData.attachments.forEach((attachment, index) => {
        console.log(
          `[EmailAPI] Attachment ${index + 1}: ${attachment.fileName}`
        );
      });
    } // Make the API call to send the email
    const emailResponse = await APIService.post(
      "/api/v1/app/sendEmail",
      emailPayload
    );

    // After successful email send, write to RecentSimulations backend
    // try {
    //   await RecentSimulationsAPI.writeActivity(emailPayload, "email");
    // } catch (activityError) {
    //   // Log the error but don't fail the email send
    //   console.error(
    //     "[EmailAPI] Failed to write to recent activities:",
    //     activityError
    //   );
    // }

    return emailResponse;
  },

  /**
   * Load pre-filled email data from session storage
   * @returns {Object|null} - The pre-filled email data or null if none exists
   */
  loadEmailDataFromStorage: () => {
    const prefilledData = sessionStorage.getItem("emailPrefilledData");

    if (prefilledData) {
      try {
        return JSON.parse(prefilledData);
      } catch (error) {
        console.error("Error parsing pre-filled email data:", error);
        return null;
      }
    }
    return null;
  },

  /**
   * Clear email data from session storage
   */
  clearEmailDataFromStorage: () => {
    sessionStorage.removeItem("emailPrefilledData");
  },

  /**
   * Store email data in session storage
   * @param {Object} emailData - The email data to store
   */ storeEmailDataInStorage: (emailData) => {
    // First clear any existing data
    sessionStorage.removeItem("emailPrefilledData");

    // Then store the new data
    sessionStorage.setItem("emailPrefilledData", JSON.stringify(emailData));
  },

  /**
   * Check if consent agreement file exists and can be accessed
   * @param {string} fileName - The name of the consent agreement file
   * @returns {Promise<boolean>} - Promise that resolves with whether the file exists
   */ /**
   * Get the best available path for the consent file
   * @param {string} fileName - The name of the consent agreement file
   * @returns {Promise<string|null>} - Promise that resolves with the best path or null if not found
   */
  getConsentFilePath: async (
    fileName = "KinShieldSimulationService-UserConsentAgreement.pdf"
  ) => {
    try {
      // Try multiple approaches to locate the file
      const possiblePaths = [
        `/${fileName}`,
        `${process.env.PUBLIC_URL}/${fileName}`,
        `/public/${fileName}`,
        `${window.location.origin}/${fileName}`,
        `${window.location.origin}/public/${fileName}`,
      ];

      console.log(`[EmailAPI] Checking for consent file using multiple paths:`);

      // Try each path until one works
      for (const path of possiblePaths) {
        try {
          console.log(`[EmailAPI] Trying path: ${path}`);
          const response = await fetch(path, { method: "HEAD" });
          if (response.ok) {
            console.log(`[EmailAPI] Found consent file at: ${path}`);
            return path;
          }
        } catch (e) {
          console.log(`[EmailAPI] Path failed: ${path}, error: ${e.message}`);
        }
      }
      console.log(
        `[EmailAPI] Could not find consent file in any location, using fallback path`
      );
      // If all HTTP checks fail, provide a hardcoded path as fallback
      const fallbackPath = `${window.location.origin}/KinShieldSimulationService-UserConsentAgreement.pdf`;
      console.log(`[EmailAPI] Using fallback path: ${fallbackPath}`);
      return fallbackPath; // Return a fallback path instead of null
    } catch (error) {
      console.error(
        `[EmailAPI] Error finding consent file path: ${error.message}`
      );
      // Still return a fallback path even if there's an error
      const fallbackPath = `${window.location.origin}/KinShieldSimulationService-UserConsentAgreement.pdf`;
      console.log(
        `[EmailAPI] Using fallback path after error: ${fallbackPath}`
      );
      return fallbackPath;
    }
  },

  checkConsentFileExists: async (
    fileName = "KinShieldSimulationService-UserConsentAgreement.pdf"
  ) => {
    try {
      const path = await EmailAPI.getConsentFilePath(fileName);
      return path !== null;
    } catch (error) {
      console.error(`[EmailAPI] Error checking consent file: ${error.message}`);
      return false;
    }
  },
};

export default EmailAPI;
